/**
 * Regional Template Service
 * 
 * CRUD operations for Regional ChartMind Templates (Position 2 prompts).
 * Regional LLM Managers create templates containing P2 prompts for each ChartMind query.
 * ChartMind Admins link to a regional template via invite code.
 * 
 * Firestore structure: regionalManagers/{managerId}/templates/{templateId}
 * 
 * Mirrors the PET (Patient Encounter Template) system at the P2 level.
 */

import { db } from '@healthdesk/shared-services';
import firebase from 'firebase/compat/app';
import { CHARTMIND_CARD_LIST } from '../chartmind/chartmindAdminService';

const COLLECTION = 'regionalManagers';
const INPUT_MODES = { PREVIOUS_PLUS_ORIGINAL: 'previous_plus_original' };

/**
 * Default P2 prompt configuration template
 */
const createDefaultP2PromptConfig = () => ({
  prompt: '',
  enabled: false,
  inputMode: INPUT_MODES.PREVIOUS_PLUS_ORIGINAL,
  lastModified: null,
  modifiedBy: null,
  version: 0,
});

/**
 * Generate an 8-character invite code (same character set as PET invite codes)
 */
function generateInviteCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

const regionalTemplateService = {
  /**
   * Create a new regional template with default (empty) P2 prompts for all ChartMind cards.
   * @param {string} managerId - The regional manager's user ID
   * @param {{ name: string, createdByEmail?: string, createdByName?: string }} data
   * @returns {Promise<Object>} Created template with id
   */
  async createTemplate(managerId, data) {
    const inviteCode = generateInviteCode();
    const name = typeof data === 'string' ? data : (data?.name || 'Untitled Regional Template');
    const createdByEmail = typeof data === 'object' ? (data?.createdByEmail || null) : null;
    const createdByName = typeof data === 'object' ? (data?.createdByName || null) : null;

    // Build default P2 prompts map (same card IDs as PET system)
    const prompts = {};
    for (const cardId of CHARTMIND_CARD_LIST) {
      prompts[cardId] = createDefaultP2PromptConfig();
    }

    const newTemplate = {
      name,
      prompts,
      inviteCode,
      createdByEmail,
      createdByName,
      // Invite controls (mirrors ChartMind Admin designee invite system)
      requiresApproval: true,       // default: require manager approval before linking
      allowedDomains: [],           // empty = no restriction; e.g. ['hospital.org']
      expiresAt: null,              // optional ISO date string
      maxUses: null,                // optional numeric cap
      usedCount: 0,                 // tracks how many times the invite code has been used
      pendingLinkedAdmins: [],      // [{ uid, email, name, requestedAt }]
      linkedAdmins: [],             // [{ uid, email, name, linkedAt }]
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    };

    const docRef = await db.collection(COLLECTION)
      .doc(managerId)
      .collection('templates')
      .add(newTemplate);

    return { id: docRef.id, ...newTemplate };
  },

  /**
   * Get all regional templates for a manager.
   * @param {string} managerId
   * @returns {Promise<Array>}
   */
  async getTemplates(managerId) {
    const snapshot = await db.collection(COLLECTION)
      .doc(managerId)
      .collection('templates')
      .orderBy('createdAt', 'desc')
      .get();

    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  /**
   * Get a single regional template.
   * @param {string} managerId
   * @param {string} templateId
   * @returns {Promise<Object|null>}
   */
  async getTemplate(managerId, templateId) {
    const doc = await db.collection(COLLECTION)
      .doc(managerId)
      .collection('templates')
      .doc(templateId)
      .get();

    if (!doc.exists) return null;
    return { id: doc.id, ...doc.data() };
  },

  /**
   * Update a template's name.
   * @param {string} managerId
   * @param {string} templateId
   * @param {string} name
   * @returns {Promise<void>}
   */
  async updateTemplateName(managerId, templateId, name) {
    await db.collection(COLLECTION)
      .doc(managerId)
      .collection('templates')
      .doc(templateId)
      .update({
        name,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
  },

  /**
   * Update a P2 prompt for a specific card within a regional template.
   * @param {string} managerId
   * @param {string} templateId
   * @param {string} cardId - e.g. 'ddx', 'chart-generation'
   * @param {Object} promptData - { prompt, enabled, inputMode?, ... }
   * @returns {Promise<void>}
   */
  async updateTemplatePrompt(managerId, templateId, cardId, promptData) {
    if (!CHARTMIND_CARD_LIST.includes(cardId)) {
      throw new Error(`Invalid card ID: ${cardId}`);
    }

    const updatePath = `prompts.${cardId}`;
    await db.collection(COLLECTION)
      .doc(managerId)
      .collection('templates')
      .doc(templateId)
      .update({
        [updatePath]: {
          ...promptData,
          lastModified: firebase.firestore.FieldValue.serverTimestamp(),
        },
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
  },

  /**
   * Delete a regional template.
   * @param {string} managerId
   * @param {string} templateId
   * @returns {Promise<void>}
   */
  async deleteTemplate(managerId, templateId) {
    await db.collection(COLLECTION)
      .doc(managerId)
      .collection('templates')
      .doc(templateId)
      .delete();
  },

  /**
   * Regenerate invite code for a template.
   * @param {string} managerId
   * @param {string} templateId
   * @returns {Promise<string>} The new invite code
   */
  async regenerateInviteCode(managerId, templateId) {
    const newCode = generateInviteCode();
    await db.collection(COLLECTION)
      .doc(managerId)
      .collection('templates')
      .doc(templateId)
      .update({
        inviteCode: newCode,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    return newCode;
  },

  /**
   * Find manager + template by regional template invite code.
   * Uses collectionGroup query on 'templates' filtered by inviteCode.
   * Extracts managerId from the document path.
   * @param {string} code - Template invite code
   * @returns {Promise<Object|null>} { manager: { id, name, email }, template } or null
   */
  async findTemplateByInviteCode(code) {
    if (!code) return null;

    // collectionGroup queries across all 'templates' subcollections, including PET templates.
    // We filter by inviteCode; regional template codes are distinct from PET codes.
    // To be safe we also check the parent collection is 'regionalManagers'.
    const snapshot = await db.collectionGroup('templates')
      .where('inviteCode', '==', code)
      .limit(5)
      .get();

    if (snapshot.empty) return null;

    // Find the doc whose parent collection is 'regionalManagers'
    const matchDoc = snapshot.docs.find(doc => {
      const parts = doc.ref.path.split('/');
      return parts[0] === COLLECTION;
    });

    if (!matchDoc) return null;

    const templateData = matchDoc.data();
    const template = { id: matchDoc.id, ...templateData };

    // Extract managerId from path: regionalManagers/{managerId}/templates/{templateId}
    const pathParts = matchDoc.ref.path.split('/');
    const managerId = pathParts[1];

    const manager = {
      id: managerId,
      name: templateData.createdByName || 'Unknown Manager',
      email: templateData.createdByEmail || '',
    };

    return { manager, template };
  },

  // ============================================
  // INVITE LINK USAGE & APPROVAL QUEUE
  // ============================================

  /**
   * Use an invite link (process a link request from a ChartMind Admin).
   * Mirrors chartmindAdminService.useInviteLink() -- checks domain, expiration,
   * max uses, deduplication, then either adds to pendingLinkedAdmins or directly links.
   *
   * @param {string} code - Invite code
   * @param {{ uid: string, email: string, name: string }} adminInfo - The ChartMind Admin using the link
   * @returns {Promise<Object>} { success, message, requiresApproval?, managerId?, templateName? }
   */
  async useInviteLink(code, adminInfo) {
    const result = await this.findTemplateByInviteCode(code);
    if (!result) {
      return { success: false, message: 'Invalid invite code' };
    }

    const { manager, template } = result;
    const normalizedEmail = (adminInfo.email || '').trim().toLowerCase();

    // 1. Email domain restriction
    const allowedDomains = template.allowedDomains;
    if (Array.isArray(allowedDomains) && allowedDomains.length > 0) {
      const emailDomain = normalizedEmail.split('@')[1]?.toLowerCase();
      const isAllowed = allowedDomains.some(d => d.toLowerCase().trim() === emailDomain);
      if (!isAllowed) {
        return {
          success: false,
          message: `This invite is only valid for email addresses from: ${allowedDomains.join(', ')}`,
        };
      }
    }

    // 2. Check if already linked
    if ((template.linkedAdmins || []).some(a => a.uid === adminInfo.uid)) {
      return { success: false, message: 'You are already linked to this regional template' };
    }

    // 3. Check if already pending
    if ((template.pendingLinkedAdmins || []).some(a => a.uid === adminInfo.uid)) {
      return { success: false, message: 'Your request is already pending approval' };
    }

    // 4. Check expiration
    if (template.expiresAt && new Date(template.expiresAt) < new Date()) {
      return { success: false, message: 'This invite link has expired' };
    }

    // 5. Check max uses
    if (template.maxUses !== null && template.maxUses !== undefined && (template.usedCount || 0) >= template.maxUses) {
      return { success: false, message: 'This invite link has reached its usage limit' };
    }

    // 6. Increment usage count
    const templateRef = db.collection(COLLECTION)
      .doc(manager.id)
      .collection('templates')
      .doc(template.id);

    await templateRef.update({
      usedCount: (template.usedCount || 0) + 1,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

    // 7. Add as linked or pending based on approval requirement
    const requiresApproval = template.requiresApproval !== false; // default true
    if (requiresApproval) {
      const pendingEntry = {
        uid: adminInfo.uid,
        email: normalizedEmail,
        name: adminInfo.name || '',
        requestedAt: new Date().toISOString(),
      };
      await templateRef.update({
        pendingLinkedAdmins: firebase.firestore.FieldValue.arrayUnion(pendingEntry),
      });
      return {
        success: true,
        message: 'Request submitted. Awaiting Regional Manager approval.',
        requiresApproval: true,
        managerId: manager.id,
        templateName: template.name,
      };
    } else {
      // Instant link
      const linkedEntry = {
        uid: adminInfo.uid,
        email: normalizedEmail,
        name: adminInfo.name || '',
        linkedAt: new Date().toISOString(),
      };
      await templateRef.update({
        linkedAdmins: firebase.firestore.FieldValue.arrayUnion(linkedEntry),
      });
      // Write linkedRegionalTemplate to the admin's chartmindAdmins doc
      await db.collection('chartmindAdmins').doc(adminInfo.uid).set({
        linkedRegionalTemplate: {
          managerId: manager.id,
          templateId: template.id,
          templateName: template.name || 'Untitled Regional Template',
          managerName: manager.name || 'Unknown Manager',
          linkedAt: new Date().toISOString(),
        },
      }, { merge: true });
      return {
        success: true,
        message: 'Regional Template linked successfully.',
        requiresApproval: false,
        managerId: manager.id,
        templateName: template.name,
      };
    }
  },

  /**
   * Approve a pending admin -- moves from pendingLinkedAdmins to linkedAdmins
   * and writes linkedRegionalTemplate to the admin's chartmindAdmins doc.
   * @param {string} managerId
   * @param {string} templateId
   * @param {string} adminUid - The ChartMind Admin's user ID
   * @returns {Promise<void>}
   */
  async approvePendingAdmin(managerId, templateId, adminUid) {
    const templateRef = db.collection(COLLECTION)
      .doc(managerId)
      .collection('templates')
      .doc(templateId);

    const doc = await templateRef.get();
    if (!doc.exists) throw new Error('Template not found');
    const data = doc.data();

    const pending = (data.pendingLinkedAdmins || []).find(a => a.uid === adminUid);
    if (!pending) throw new Error('Pending request not found');

    // Remove from pending
    const updatedPending = (data.pendingLinkedAdmins || []).filter(a => a.uid !== adminUid);

    // Add to linked
    const linkedEntry = {
      uid: pending.uid,
      email: pending.email,
      name: pending.name,
      linkedAt: new Date().toISOString(),
    };
    const updatedLinked = [...(data.linkedAdmins || []), linkedEntry];

    await templateRef.update({
      pendingLinkedAdmins: updatedPending,
      linkedAdmins: updatedLinked,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

    // Write linkedRegionalTemplate to the admin's chartmindAdmins doc
    await db.collection('chartmindAdmins').doc(adminUid).set({
      linkedRegionalTemplate: {
        managerId,
        templateId,
        templateName: data.name || 'Untitled Regional Template',
        managerName: data.createdByName || 'Unknown Manager',
        linkedAt: linkedEntry.linkedAt,
      },
    }, { merge: true });
  },

  /**
   * Reject a pending admin -- removes from pendingLinkedAdmins.
   * @param {string} managerId
   * @param {string} templateId
   * @param {string} adminUid
   * @returns {Promise<void>}
   */
  async rejectPendingAdmin(managerId, templateId, adminUid) {
    const templateRef = db.collection(COLLECTION)
      .doc(managerId)
      .collection('templates')
      .doc(templateId);

    const doc = await templateRef.get();
    if (!doc.exists) throw new Error('Template not found');
    const data = doc.data();

    const updatedPending = (data.pendingLinkedAdmins || []).filter(a => a.uid !== adminUid);

    await templateRef.update({
      pendingLinkedAdmins: updatedPending,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  },

  /**
   * Unlink an approved admin -- removes from linkedAdmins and clears
   * linkedRegionalTemplate on the admin's chartmindAdmins doc.
   * @param {string} managerId
   * @param {string} templateId
   * @param {string} adminUid
   * @returns {Promise<void>}
   */
  async unlinkAdmin(managerId, templateId, adminUid) {
    const templateRef = db.collection(COLLECTION)
      .doc(managerId)
      .collection('templates')
      .doc(templateId);

    const doc = await templateRef.get();
    if (!doc.exists) throw new Error('Template not found');
    const data = doc.data();

    const updatedLinked = (data.linkedAdmins || []).filter(a => a.uid !== adminUid);

    await templateRef.update({
      linkedAdmins: updatedLinked,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

    // Clear linkedRegionalTemplate on the admin doc (only if it points to this template)
    try {
      const adminDoc = await db.collection('chartmindAdmins').doc(adminUid).get();
      const linked = adminDoc.exists ? adminDoc.data()?.linkedRegionalTemplate : null;
      if (linked?.managerId === managerId && linked?.templateId === templateId) {
        await db.collection('chartmindAdmins').doc(adminUid).update({
          linkedRegionalTemplate: firebase.firestore.FieldValue.delete(),
        });
      }
    } catch (err) {
      console.warn('[regionalTemplateService] Failed to clear admin linked template:', err);
    }
  },

  // ============================================
  // INVITE SETTINGS
  // ============================================

  /**
   * Update allowed email domains for a regional template.
   * @param {string} managerId
   * @param {string} templateId
   * @param {string[]} domains - e.g. ['hospital.org'] or [] for no restriction
   * @returns {Promise<void>}
   */
  async updateAllowedDomains(managerId, templateId, domains) {
    const normalized = (domains || []).map(d => d.toLowerCase().trim()).filter(Boolean);
    await db.collection(COLLECTION)
      .doc(managerId)
      .collection('templates')
      .doc(templateId)
      .update({
        allowedDomains: normalized,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
  },

  /**
   * Update invite settings (requiresApproval, expiresAt, maxUses).
   * @param {string} managerId
   * @param {string} templateId
   * @param {{ requiresApproval?: boolean, expiresAt?: string|null, maxUses?: number|null }} settings
   * @returns {Promise<void>}
   */
  async updateInviteSettings(managerId, templateId, settings) {
    const update = { updatedAt: firebase.firestore.FieldValue.serverTimestamp() };
    if (settings.requiresApproval !== undefined) update.requiresApproval = settings.requiresApproval;
    if (settings.expiresAt !== undefined) update.expiresAt = settings.expiresAt;
    if (settings.maxUses !== undefined) update.maxUses = settings.maxUses;

    await db.collection(COLLECTION)
      .doc(managerId)
      .collection('templates')
      .doc(templateId)
      .update(update);
  },

  /**
   * Build the full invite link URL for a regional template.
   * @param {string} inviteCode
   * @returns {string}
   */
  buildInviteLink(inviteCode) {
    const baseUrl = window.location.origin;
    return `${baseUrl}/join/r_${inviteCode}`;
  },
};

export default regionalTemplateService;
